import React, { useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Alert, Button, Checkbox, InputField, Modal, ModalContent, ModalFooter } from '@freelancelabs/components';

import RefusalNotice from '../../components/RefusalNotice';
import { ListSkeleton } from '../../components/Skeletons';
import { DataScope } from '../../graphql/__generated__/schema';
import {
    AccountActivitiesDocument,
    MembershipPerimeterSelectionDocument,
    UpdateMembershipPerimeterDocument,
} from '../../graphql/__generated__/types';
import ActivityPicker from './onboarding/ActivityPicker';
import { perimeterRefusalOf } from './perimeterRefusal';

/** La même valeur que les deux recherches du portail et que l'onboarding : deux champs qui
 *  réagissent à des vitesses visiblement différentes se lisent comme deux contrôles. */
const SEARCH_DEBOUNCE_MS = 300;

interface Props {
    /** Le rattachement dont on modifie le périmètre. */
    membershipId: string;
    /** Le nom du compte, pour que le dialog dise sur quel compte il agit. */
    accountName: string;
    onClose: () => void;
    /** Appelé après un enregistrement réussi : la fiche recharge son périmètre. */
    onSaved: () => void;
}

/**
 * Modifier le périmètre d'un rattachement (PCG-357).
 *
 * **La mutation prend l'ÉTAT VOULU et non un différentiel** : une case décochée veut dire
 * « retire-la ». Le dialog doit donc partir de la sélection COURANTE et complète, que
 * `membershipPerimeterSelection` sert : l'aperçu de la fiche est plafonné à vingt entrées et
 * ne suffirait pas à pré-cocher sans risquer d'effacer ce qu'il ne montre pas.
 *
 * **La sélection vit ici, indépendante de ce que la liste rend.** `accountActivities` est
 * cherchée puis rétrécie par la recherche, donc le sélecteur n'affiche jamais forcément tout
 * ce qui est coché. Les ids cochés hors du sous-ensemble affiché restent dans l'état et
 * repartent à l'envoi : sans quoi une recherche effacerait le périmètre qu'elle ne montre pas.
 *
 * **« Toutes les activités » est la portée ALL.** Cochée, le sélecteur disparaît et l'envoi
 * part avec des listes vides ; décochée, on restreint et il faut au moins une activité. Passer
 * de l'une à l'autre ne vide pas la sélection en mémoire : revenir à « restreindre » retrouve
 * ce qui était coché.
 *
 * **Le champ de recherche vit AU-DESSUS du sélecteur, jamais dedans** : monté sous ce qu'il
 * filtre, une requête en vol le démonterait à chaque touche. Même règle que l'onboarding.
 */
const EditPerimeterDialog = ({ membershipId, accountName, onClose, onSaved }: Props) => {
    const {
        data: selectionData,
        loading: selectionLoading,
        error: selectionError,
    } = useQuery(MembershipPerimeterSelectionDocument, { variables: { membershipId }, fetchPolicy: 'network-only' });

    const selection = selectionData?.membershipPerimeterSelection ?? null;

    // L'état voulu, initialisé depuis la sélection courante une fois lue. `initialised` évite
    // de réinitialiser à chaque rendu : l'opérateur perdrait ses cases dès la première frappe.
    const [scope, setScope] = useState<DataScope>(DataScope.All);
    const [needIds, setNeedIds] = useState<string[]>([]);
    const [serviceIds, setServiceIds] = useState<string[]>([]);
    const [initialised, setInitialised] = useState(false);

    useEffect(() => {
        if (!selection || initialised) {
            return;
        }
        setScope(selection.scope ?? DataScope.All);
        setNeedIds(selection.needIds ?? []);
        setServiceIds(selection.serviceIds ?? []);
        setInitialised(true);
    }, [selection, initialised]);

    const restricted = scope === DataScope.Restricted;

    const [search, setSearch] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearch(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    /**
     * L'univers cochable du compte. Le compte voyage en ARGUMENT (jamais en `x-organization-id`) :
     * cet écran agit sur un compte qui n'est pas celui de l'acteur, et l'en-tête ferait échouer
     * l'appel sur un refus qui se lit comme un bug d'autorisation. `email` vient de la sélection,
     * qui le porte pour cette raison. On ne cherche qu'une fois restreint et la sélection lue.
     */
    const {
        data: activityData,
        previousData: previousActivityData,
        loading: activitiesLoading,
        error: activitiesError,
        refetch: refetchActivities,
    } = useQuery(AccountActivitiesDocument, {
        variables: {
            organizationId: selection?.organizationId ?? '',
            email: selection?.email ?? '',
            search: debouncedSearch,
        },
        skip: !restricted || !selection,
    });

    // La liste précédente tient pendant que la suivante arrive : garder le sélecteur sous les
    // yeux vaut mieux que le remplacer par un squelette à chaque mot tapé.
    const activities = activityData?.accountActivities ?? previousActivityData?.accountActivities ?? null;

    const selectedCount = needIds.length + serviceIds.length;

    const toggleNeed = (needId: string, checked: boolean) =>
        setNeedIds((prev) => (checked ? [...prev, needId] : prev.filter((id) => id !== needId)));

    const toggleService = (serviceId: string, checked: boolean) =>
        setServiceIds((prev) => (checked ? [...prev, serviceId] : prev.filter((id) => id !== serviceId)));

    // Cocher un marché coche ses PRESTATIONS affichées, et c'est tout ce qui est enregistré : le
    // marché lui-même ne l'est jamais. Sous une recherche, il ne coche que ce qui est à l'écran.
    const toggleMarket = (ids: string[], checked: boolean) =>
        setServiceIds((prev) => (checked ? [...new Set([...prev, ...ids])] : prev.filter((id) => !ids.includes(id))));

    // La fiche relit son périmètre : `onSaved` déclenche le refetch de PortalUser côté parent,
    // qui seul connaît le `userId`. Les accordéons et les compteurs en descendent.
    const [save, { loading: saving, error: saveError }] = useMutation(UpdateMembershipPerimeterDocument);

    // Une restriction sans aucune activité serait refusée (elle cacherait tout) : on désactive
    // l'envoi avec sa raison plutôt que de laisser partir un appel voué au refus.
    const emptyRestriction = restricted && selectedCount === 0;

    const submit = () => {
        save({
            variables: {
                membershipId,
                scope,
                needIds: restricted ? needIds : [],
                serviceIds: restricted ? serviceIds : [],
            },
        })
            .then((result) => {
                if (result.data) {
                    onSaved();
                    onClose();
                }
            })
            .catch(() => {});
    };

    return (
        <Modal open onClose={onClose} title={c('Perimeter').t`Change the perimeter`} size={760}>
            <ModalContent>
                <div className="flex flex-col gap-4">
                    <p className="text-sm text-text-600">
                        {c('Perimeter').t`On the account`} <b className="text-text-900">{accountName}</b>
                    </p>

                    {selectionLoading && !selection && <ListSkeleton rows={4} withControl />}

                    {selectionError && (
                        <Alert type="warning">
                            {c('Perimeter')
                                .t`The current perimeter could not be read, so nothing can be changed safely. Close this and try again.`}
                        </Alert>
                    )}

                    {selection && (
                        <>
                            {/* « Toutes les activités » EN TÊTE : cochée, la personne voit tout le
                                compte et le sélecteur disparaît ; décochée, on restreint. */}
                            <label className="flex items-start gap-2 text-sm text-text-900">
                                <Checkbox
                                    id="perimeter-all"
                                    checked={scope === DataScope.All}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        setScope(event.target.checked ? DataScope.All : DataScope.Restricted)
                                    }
                                >
                                    <span className="flex flex-col">
                                        <span>{c('Perimeter').t`All the account's activities`}</span>
                                        <span className="text-xs text-text-600">
                                            {c('Perimeter')
                                                .t`The person sees everything on this account. Uncheck to limit them to chosen activities.`}
                                        </span>
                                    </span>
                                </Checkbox>
                            </label>

                            {restricted && (
                                <div className="border-t border-border-norm pt-4">
                                    <div className="mb-3 flex flex-wrap items-center gap-3">
                                        <div className="grow">
                                            <InputField
                                                id="perimeter-search"
                                                label={c('Perimeter').t`Search an activity`}
                                                type="search"
                                                value={search}
                                                onValue={setSearch}
                                            />
                                        </div>
                                        <span className="shrink-0 rounded-full border border-border-norm px-3 py-1 text-xs text-text-600">
                                            {selectedCount === 0
                                                ? c('Perimeter').t`No activity selected`
                                                : c('Perimeter').t`${selectedCount} selected`}
                                        </span>
                                    </div>

                                    {activitiesLoading && !activities && <ListSkeleton rows={6} withControl />}

                                    {activitiesError && (
                                        <div className="mb-3 flex flex-wrap items-center gap-3">
                                            <p className="text-xs text-danger-700" role="alert">
                                                {c('Perimeter')
                                                    .t`The activities of this account could not be read, so nothing is listed.`}
                                            </p>
                                            <Button category="weak" size="small" onClick={() => refetchActivities()}>
                                                {c('Perimeter').t`Try again`}
                                            </Button>
                                        </div>
                                    )}

                                    {activities && (
                                        <ActivityPicker
                                            activities={activities}
                                            needIds={needIds}
                                            serviceIds={serviceIds}
                                            onToggleNeed={toggleNeed}
                                            onToggleService={toggleService}
                                            onToggleMarket={toggleMarket}
                                        />
                                    )}
                                </div>
                            )}

                            {saveError && <RefusalNotice refusal={perimeterRefusalOf(saveError)} />}
                        </>
                    )}
                </div>
            </ModalContent>

            <ModalFooter>
                <Button category="weak" onClick={onClose} disabled={saving}>
                    {c('Action').t`Cancel`}
                </Button>
                <Button
                    category="default"
                    loading={saving}
                    disabled={!selection || emptyRestriction}
                    title={
                        emptyRestriction
                            ? c('Perimeter').t`Tick at least one activity, or grant the whole account.`
                            : undefined
                    }
                    onClick={submit}
                >
                    {c('Action').t`Save`}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditPerimeterDialog;
